import React, { useContext, useState } from 'react';
import { AuthContext } from '@/App';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import {
  Users2,
  Sparkles,
  Link2,
  Copy,
  Share2,
  QrCode,
  MousePointerClick,
  UserCheck,
  Clock,
  Coins,
  Percent,
  Calendar,
  CheckCircle2,
  Circle,
  ShieldCheck,
  Trophy,
  ArrowRight,
  Wallet,
  Repeat
} from 'lucide-react';

// Wave 1 mock data
const MOCK = {
  referralCode: 'PHOENYX01',
  referralUrl: 'consistency.app/r/PHOENYX01',
  stats: {
    clicks: 124,
    conversions: 9,
    pending: '$187',
    paidOut: '$620'
  },
  payout: {
    pending: '$87',
    nextEstimated: 'June 15',
    last: '$143.20'
  },
  commission: {
    rate: '30%',
    months: 12,
    cadence: 'Monthly'
  }
};

const PRE_REQUIREMENTS = [
  { key: 'subscription', label: 'Active Subscription', met: true },
  { key: 'payout', label: 'Payout Setup Complete', met: false },
  { key: 'standing', label: 'Account in Good Standing', met: true }
];

// ---------------- Reusable ----------------
const StatCard = ({ icon: Icon, accent, label, value, testId }) => (
  <Card className="glass border-white/5" data-testid={testId}>
    <CardContent className="p-3">
      <div className="flex items-center gap-2 mb-1.5">
        <div className={`w-6 h-6 rounded-md bg-${accent}-500/10 border border-white/5 flex items-center justify-center`}>
          <Icon className={`h-3 w-3 text-${accent}-400`} />
        </div>
        <p className="text-[10px] uppercase tracking-wider text-[#8A93A6]">{label}</p>
      </div>
      <p className="text-xl font-bold text-white tabular-nums leading-tight">{value}</p>
    </CardContent>
  </Card>
);

const ShareWinPill = ({ accent = 'pink', label = 'Share Win', preview, testId }) => (
  <div className="flex items-center gap-2 flex-wrap" data-testid={testId}>
    <button
      type="button"
      className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-${accent}-500/10 border border-${accent}-500/25 text-[10px] font-medium text-${accent}-200 hover:bg-${accent}-500/15 transition-colors`}
    >
      <Share2 className="h-3 w-3" />
      {label}
    </button>
    {preview && (
      <span className="text-[10px] text-[#8A93A6] truncate flex-1 min-w-0 italic">
        “{preview}”
      </span>
    )}
  </div>
);

const StatusPill = ({ status }) => {
  const map = {
    active: { label: 'Active', cls: 'border-green-500/30 text-green-300 bg-green-500/10' },
    setup: { label: 'Setup Required', cls: 'border-amber-500/30 text-amber-300 bg-amber-500/10' },
    payout: { label: 'Payout Setup Required', cls: 'border-amber-500/30 text-amber-300 bg-amber-500/10' },
    restricted: { label: 'Restricted', cls: 'border-red-500/30 text-red-300 bg-red-500/10' }
  };
  const e = map[status] || map.setup;
  return (
    <Badge variant="outline" className={`text-[10px] uppercase tracking-wider ${e.cls}`}>
      {e.label}
    </Badge>
  );
};

// ---------------- Page ----------------
export default function Affiliates() {
  const { user } = useContext(AuthContext);
  const isAdmin = !!user?.is_internal_admin;

  // Wave 1: Admin always sees activated dashboard. Other users default to pre-activation.
  const [isActive, setIsActive] = useState(isAdmin);

  const allMet = PRE_REQUIREMENTS.every((r) => r.met);

  const handleActivate = () => {
    if (!allMet) {
      toast.error('Complete remaining requirements to activate.');
      return;
    }
    setIsActive(true);
    toast.success('Affiliate dashboard activated');
  };

  const handleCopy = () => {
    if (navigator?.clipboard) {
      navigator.clipboard.writeText(MOCK.referralUrl).catch(() => {});
    }
    toast.success('Referral link copied');
  };

  const status = isActive ? 'active' : allMet ? 'setup' : 'payout';

  return (
    <div className="container mx-auto p-6 max-w-5xl" data-testid="affiliates-page">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-wrap items-center gap-3 mb-2">
          <Users2 className="h-6 w-6 text-pink-400" />
          <h1 className="text-3xl font-bold text-white">Affiliates</h1>
          <Badge variant="outline" className="text-[10px] border-pink-500/30 text-pink-300 bg-pink-500/10 uppercase tracking-wider font-semibold">
            Program + Dashboard
          </Badge>
          {isAdmin && (
            <Badge variant="outline" className="text-[10px] border-pink-500/30 text-pink-300 bg-pink-500/10 uppercase tracking-wider font-semibold">
              Admin Preview
            </Badge>
          )}
        </div>
        <p className="text-[#8A93A6] max-w-2xl">
          Earn recurring revenue by helping creators grow on Consistency.
        </p>
      </div>

      {/* Affiliate Status Card (always visible) */}
      <Card className="glass border-white/5 mb-6" data-testid="affiliate-status-card">
        <CardContent className="p-4">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-pink-500/10 border border-white/5 flex items-center justify-center">
                <Sparkles className="h-5 w-5 text-pink-400" />
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <p className="text-sm font-semibold text-white">Affiliate Status</p>
                  <StatusPill status={status} />
                </div>
                <p className="text-[11px] text-[#8A93A6] max-w-md leading-relaxed">
                  {isActive
                    ? 'Your affiliate dashboard is live. Share your link to start earning recurring commissions.'
                    : 'Earn recurring commissions by sharing Consistency. No application required.'}
                </p>
              </div>
            </div>

            {!isActive && (
              <Button
                onClick={handleActivate}
                disabled={!allMet}
                className="bg-pink-500 hover:bg-pink-600 text-white"
                data-testid="activate-affiliate-btn"
              >
                Activate Affiliate Dashboard
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            )}
          </div>

          {!isActive && (
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-2" data-testid="affiliate-requirements">
              {PRE_REQUIREMENTS.map((r) => (
                <div
                  key={r.key}
                  className={`flex items-center gap-2 rounded-md border px-2.5 py-1.5 ${
                    r.met ? 'border-green-500/25 bg-green-500/[0.05]' : 'border-white/5 bg-white/[0.02]'
                  }`}
                  data-testid={`affiliate-req-${r.key}`}
                >
                  {r.met ? (
                    <CheckCircle2 className="h-3.5 w-3.5 text-green-400 shrink-0" />
                  ) : (
                    <Circle className="h-3.5 w-3.5 text-[#555E6E] shrink-0" />
                  )}
                  <span className={`text-[11px] ${r.met ? 'text-white' : 'text-[#8A93A6]'}`}>{r.label}</span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ACTIVE DASHBOARD */}
      {isActive && (
        <>
          {/* 1. Referral Link */}
          <Card className="glass border-white/5 mb-4" data-testid="referral-link-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <Link2 className="h-4 w-4 text-pink-400" />
                <p className="text-sm font-semibold text-white">Your Referral Link</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 items-stretch">
                <div className="rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 flex items-center justify-between">
                  <span className="text-sm text-[#C7CED9] font-mono truncate">{MOCK.referralUrl}</span>
                  <Badge variant="outline" className="text-[10px] border-white/15 text-[#8A93A6] bg-white/[0.03] uppercase tracking-wider ml-2 shrink-0">
                    Code: {MOCK.referralCode}
                  </Badge>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" onClick={handleCopy} data-testid="copy-referral-btn">
                    <Copy className="h-4 w-4 mr-2" />
                    Copy Link
                  </Button>
                  <Button className="bg-pink-500 hover:bg-pink-600 text-white" data-testid="share-referral-btn">
                    <Share2 className="h-4 w-4 mr-2" />
                    Share
                  </Button>
                </div>
              </div>

              {/* QR code placeholder */}
              <div className="mt-3 flex items-center gap-2 text-[11px] text-[#8A93A6]">
                <div className="w-9 h-9 rounded border border-white/10 bg-white/[0.02] flex items-center justify-center">
                  <QrCode className="h-4 w-4 text-[#8A93A6]" />
                </div>
                <span>QR code available for posters & live streams (coming soon).</span>
              </div>
            </CardContent>
          </Card>

          {/* 2. Performance Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4" data-testid="affiliate-performance">
            <StatCard icon={MousePointerClick} accent="cyan" label="Clicks" value={MOCK.stats.clicks} testId="stat-clicks" />
            <StatCard icon={UserCheck} accent="green" label="Conversions" value={MOCK.stats.conversions} testId="stat-conversions" />
            <StatCard icon={Clock} accent="amber" label="Pending Earnings" value={MOCK.stats.pending} testId="stat-pending" />
            <StatCard icon={Coins} accent="pink" label="Paid Out" value={MOCK.stats.paidOut} testId="stat-paid" />
          </div>

          {/* 3. Commission Structure + 4. Payout Status (side-by-side) */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
            {/* 3. Commission */}
            <Card className="glass border-white/5" data-testid="commission-structure-card">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Percent className="h-4 w-4 text-pink-400" />
                  <p className="text-sm font-semibold text-white">Commission Structure</p>
                </div>
                <div className="flex items-baseline gap-2 mb-2">
                  <span className="text-3xl font-bold text-white tabular-nums">{MOCK.commission.rate}</span>
                  <span className="text-xs text-[#8A93A6]">recurring commission</span>
                </div>
                <div className="grid grid-cols-2 gap-2 mb-3">
                  <div className="rounded-md border border-white/5 bg-white/[0.02] px-2.5 py-1.5">
                    <p className="text-[9px] uppercase tracking-wider text-[#8A93A6]">Duration</p>
                    <p className="text-xs text-white">Up to {MOCK.commission.months} months</p>
                  </div>
                  <div className="rounded-md border border-white/5 bg-white/[0.02] px-2.5 py-1.5">
                    <p className="text-[9px] uppercase tracking-wider text-[#8A93A6]">Cadence</p>
                    <p className="text-xs text-white">{MOCK.commission.cadence} payouts</p>
                  </div>
                </div>
                <p className="text-[11px] text-[#8A93A6] leading-relaxed">
                  Earn recurring commissions when creators subscribe through your referral link.
                </p>
              </CardContent>
            </Card>

            {/* 4. Payout Status */}
            <Card className="glass border-white/5" data-testid="payout-status-card">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Wallet className="h-4 w-4 text-cyan-400" />
                  <p className="text-sm font-semibold text-white">Payout Status</p>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between rounded-md border border-white/5 bg-white/[0.02] px-3 py-2">
                    <span className="text-xs text-[#8A93A6]">Pending</span>
                    <span className="text-sm font-semibold text-amber-300 tabular-nums">{MOCK.payout.pending}</span>
                  </div>
                  <div className="flex items-center justify-between rounded-md border border-white/5 bg-white/[0.02] px-3 py-2">
                    <span className="text-xs text-[#8A93A6] flex items-center gap-1.5">
                      <Calendar className="h-3 w-3" /> Next Estimated Payout
                    </span>
                    <span className="text-sm font-semibold text-white">{MOCK.payout.nextEstimated}</span>
                  </div>
                  <div className="flex items-center justify-between rounded-md border border-white/5 bg-white/[0.02] px-3 py-2">
                    <span className="text-xs text-[#8A93A6]">Last Payout</span>
                    <span className="text-sm font-semibold text-green-300 tabular-nums">{MOCK.payout.last}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* 5. Shareable Affiliate Wins */}
          <Card className="glass border-white/5 mb-4" data-testid="affiliate-wins-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <Trophy className="h-4 w-4 text-amber-400" />
                <p className="text-sm font-semibold text-white">Shareable Affiliate Wins</p>
              </div>
              <div className="space-y-1.5">
                <ShareWinPill
                  accent="pink"
                  label="Share Win"
                  preview="🎉 I just earned my first affiliate payout on Consistency."
                  testId="aff-win-first-payout"
                />
                <ShareWinPill
                  accent="cyan"
                  label="Share Win"
                  preview="I helped 5 creators start growing on Consistency."
                  testId="aff-win-helped-creators"
                />
                <ShareWinPill
                  accent="amber"
                  label="Share Win"
                  preview="I unlocked my first referral milestone."
                  testId="aff-win-milestone"
                />
              </div>
            </CardContent>
          </Card>

          {/* 6. How Affiliate Earnings Work */}
          <Card className="glass border-white/5 mb-4" data-testid="affiliate-how-it-works">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <Repeat className="h-4 w-4 text-violet-400" />
                <p className="text-sm font-semibold text-white">How Affiliate Earnings Work</p>
              </div>
              <ol className="grid grid-cols-1 md:grid-cols-4 gap-2">
                {[
                  { n: 1, t: 'Share your referral link', icon: Link2 },
                  { n: 2, t: 'New user subscribes', icon: UserCheck },
                  { n: 3, t: 'Revenue clears verification', icon: ShieldCheck },
                  { n: 4, t: 'Monthly payout issued', icon: Coins }
                ].map((s) => {
                  const SIcon = s.icon;
                  return (
                    <li
                      key={s.n}
                      className="rounded-md border border-white/5 bg-white/[0.02] px-3 py-2"
                      data-testid={`how-step-${s.n}`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-[9px] uppercase tracking-wider text-pink-300 font-semibold tabular-nums">Step {s.n}</span>
                        <SIcon className="h-3 w-3 text-[#8A93A6]" />
                      </div>
                      <p className="text-xs text-white leading-snug">{s.t}</p>
                    </li>
                  );
                })}
              </ol>
            </CardContent>
          </Card>

          {/* Conversion progress mini-bar (subtle accent) */}
          <div className="mb-2" data-testid="affiliate-conversion-progress">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] uppercase tracking-wider text-[#8A93A6]">Conversion Rate</span>
              <span className="text-[10px] text-[#C7CED9] tabular-nums">
                {((MOCK.stats.conversions / MOCK.stats.clicks) * 100).toFixed(1)}%
              </span>
            </div>
            <Progress value={(MOCK.stats.conversions / MOCK.stats.clicks) * 100} className="h-1 bg-white/[0.04]" />
          </div>
        </>
      )}
    </div>
  );
}
